// ============================================
// ModalAvisarResponsable.js — "Jalar al responsable" (CONTROL-EXP Ajuste 3)
// ============================================
// Direccion, desde un expediente atrasado (en el panel de Expedientes o en el
// resumen de Mi Dia), avisa al responsable SIN salir de la pantalla.
//
// Canal: CORREO (mailto:) con el correo del responsable — es el unico dato de
// contacto que existe en `usuarios` (no hay telefono/WhatsApp ni mensajeria
// interna persona-a-persona en la app). Ademas se REGISTRA el aviso en el
// expediente (quien/cuando/que) via POST /api/control-expedientes/:id/recordatorio,
// que escribe en expediente_paso_eventos (NO altera "dias en el paso").
//
// El mensaje viene pre-armado y es EDITABLE antes de enviar.

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { authHeaders } from '../../utils/storage';
import { useUsuario } from '../../context/UsuarioContext';
import { FF, C } from '../../utils/estiloExpedientes';
import { Mail, X, Send, Clock } from 'lucide-react';
import { API_BASE } from '../../config/api';


function textoDias(n) {
  const d = Number(n) || 0;
  if (d <= 0) return 'hoy';
  if (d === 1) return '1 dia';
  return `${d} dias`;
}

function mensajePorDefecto(exp, director) {
  const resp = (exp.responsable_nombre || '').split(' ')[0] || 'compañero';
  const cliente = exp.cliente_nombre || 'el cliente';
  const paso = exp.paso_nombre || exp.estado || 'su paso actual';
  const folio = exp.folio || `#${exp.id}`;
  return `Hola ${resp}, el expediente ${folio} de ${cliente} lleva ${textoDias(exp.dias_en_paso)} en "${paso}". Necesito que lo resuelvas hoy. — ${director}`;
}

export default function ModalAvisarResponsable({ exp, onClose, onEnviado }) {
  const { usuario } = useUsuario();
  const director = (usuario && usuario.nombre) || 'Direccion';
  const [mensaje, setMensaje] = useState(() => mensajePorDefecto(exp, director));
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState(null); // texto de resultado
  const correo = exp.responsable_correo || '';

  const enviar = async () => {
    setEnviando(true);
    setAviso(null);
    try {
      // 1) Registrar el recordatorio en el expediente (deja rastro).
      const res = await fetch(`${API_BASE}/api/control-expedientes/${exp.id}/recordatorio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          nota: mensaje,
          destinatario_id: exp.vendedor_id || null,
          destinatario_correo: correo || null,
          via: 'correo',
        }),
      });
      // No confirmar en falso: si el backend no guardo, no seguimos como exito.
      if (!res.ok) throw new Error('registro');

      // 2) Abrir el correo pre-armado (canal disponible). Si no hay correo, avisar.
      if (correo) {
        const asunto = `Expediente ${exp.folio || exp.id} necesita atencion`;
        const url = `mailto:${encodeURIComponent(correo)}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(mensaje)}`;
        window.location.href = url;
        if (typeof onEnviado === 'function') onEnviado('Recordatorio registrado y correo abierto.');
        onClose();
      } else {
        setAviso('Se registro el recordatorio, pero el responsable no tiene correo en su ficha, asi que no se pudo abrir el correo. Avisale por tu medio habitual.');
      }
    } catch (e) {
      setAviso('No se pudo registrar el recordatorio. Revisa tu conexion e intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(31,41,55,.45)', zIndex: 4000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        animation: 'beepFadeUp .15s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: FF, background: C.blanco, borderRadius: 16, width: '100%', maxWidth: 460,
          boxShadow: '0 20px 60px rgba(31,41,55,.3)', overflow: 'hidden',
        }}
      >
        {/* Cabecera */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '16px 18px', borderBottom: `1px solid ${C.borde}` }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: '#eef4fb', color: C.navy, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Mail size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.oscuro }}>Avisar al responsable</div>
            <div style={{ fontSize: 12, color: C.grisClaro, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {exp.responsable_nombre || 'Sin responsable'}{correo ? ` · ${correo}` : ' · sin correo'}
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: C.grisClaro, cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Cuerpo */}
        <div style={{ padding: 18 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', padding: '3px 9px', borderRadius: 999, marginBottom: 12 }}>
            <Clock size={12} /> {exp.folio || `#${exp.id}`} · {textoDias(exp.dias_en_paso)} en {exp.paso_nombre || exp.estado}
          </div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.gris, marginBottom: 6 }}>Mensaje (puedes editarlo)</label>
          <textarea
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            rows={5}
            style={{
              width: '100%', boxSizing: 'border-box', padding: 11, borderRadius: 10, border: `1px solid ${C.borde}`,
              fontFamily: FF, fontSize: 13.5, color: C.oscuro, resize: 'vertical', outline: 'none', lineHeight: 1.5,
            }}
          />
          {aviso && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '9px 11px', lineHeight: 1.4 }}>
              {aviso}
            </div>
          )}
        </div>

        {/* Pie */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '0 18px 18px' }}>
          <button
            onClick={onClose}
            style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${C.borde}`, background: C.blanco, color: C.gris, fontFamily: FF, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            {aviso ? 'Cerrar' : 'Cancelar'}
          </button>
          <button
            onClick={enviar}
            disabled={enviando}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px', borderRadius: 10, border: 'none',
              background: enviando ? '#9db8dc' : C.navy, color: '#fff', fontFamily: FF, fontSize: 13, fontWeight: 700,
              cursor: enviando ? 'default' : 'pointer',
            }}
          >
            <Send size={15} /> {enviando ? 'Enviando…' : (correo ? 'Enviar correo' : 'Registrar aviso')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
